import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
} from 'react-hook-form-mui';
import { useNavigate } from 'react-router';
import { DeleteForever } from '@mui/icons-material';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
} from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';

import {
    BusinessInput,
    DataGrid,
    DataGridCard,
    dataGridNestedObjectValueGetter,
} from '@barboxx/admin-components';
import {
    CREATE_BUSINESS_POUR_AVAILABILITY_SCHEMA,
    ID_WIDTH,
    NAME_MIN_WIDTH,
    PRICE_WIDTH,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import {
    Business,
    BusinessPourAvailability,
    PourAvailability,
} from '@barboxx/models';
import { useBool } from '@barboxx/react-utils';

const COLUMNS: GridColDef<Business>[] = [
    {
        field: 'id',
        headerName: 'Id',
        width: ID_WIDTH,
    },
    {
        field: 'business.place.name',
        headerName: 'Base Price',
        minWidth: PRICE_WIDTH,
        valueGetter: dataGridNestedObjectValueGetter,
        flex: 1,
    },
    {
        field: 'basePrice',
        headerName: 'Base Price',
        minWidth: PRICE_WIDTH,
    },
    {
        field: 'upCharge',
        headerName: 'Up Charge',
        minWidth: PRICE_WIDTH,
    },

    {
        field: 'displayOrder',
        headerName: 'Display Order',
        minWidth: NAME_MIN_WIDTH,
    },
    {
        field: 'isDefault',
        headerName: 'Is Default',
        minWidth: NAME_MIN_WIDTH,
        type: 'boolean',
    },
];

interface Props {
    pourAvailability: PourAvailability.DetailsResponse;
    refetch: () => void;
}

export const BusinessListCard = ({ pourAvailability, refetch }: Props) => {
    const { id, businessPourAvailabilities } = pourAvailability;
    const navigate = useNavigate();
    const notifications = useNotifications();
    const [open, dialogActions] = useBool();

    const formContext = useForm<BusinessPourAvailability.CreateRequest>({
        resolver: yupResolver(CREATE_BUSINESS_POUR_AVAILABILITY_SCHEMA),
        defaultValues: {
            pourAvailabilityId: id,
            isActive: true,
            notAllowed: false,
            upCharge: 1,
            isDefault: false,
        },
    });

    const mutateCreatePourFormat = useMutation({
        mutationKey: ['businessPourAvailability'],
        mutationFn: (data: BusinessPourAvailability.CreateRequest) =>
            Api.BusinessSpecific.PourAvailability.postPourAvailability(data),
    });

    const onSubmit = async (values: BusinessPourAvailability.CreateRequest) => {
        return mutateCreatePourFormat
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created  pour format!`, {
                    severity: 'success',
                });
                handleCancelCreate();
                refetch();
            })
            .catch(e => {
                notifications.show('Error creating pour format!', {
                    severity: 'error',
                });
            });
    };

    const mutateDeletePourAvailability = useMutation({
        mutationKey: ['deleteBusinessPourAvailability'],
        mutationFn: (data: BusinessPourAvailability.DeleteRequest) =>
            Api.BusinessSpecific.PourAvailability.deletePourAvailability(data),
    });

    const handleDeletePourAvailability = async (
        values: BusinessPourAvailability.DeleteRequest
    ) => {
        return mutateDeletePourAvailability
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Deleted pour availability!`, {
                    severity: 'success',
                });
                refetch();
            })
            .catch(e => {
                notifications.show('Error deleting pour availability!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        dialogActions.off();
    };

    const handleRowClick = (params: GridRowParams) => {
        navigate(`${params.id}`);
    };

    const columns = COLUMNS.concat([
        {
            field: 'actions',
            headerName: 'Actions',
            minWidth: NAME_MIN_WIDTH,
            type: 'actions',
            getActions: params => [
                // <Button
                //     key="edit"
                //     onClick={() => {
                //         navigate(`${params.id}`);
                //     }}
                // >
                //     Edit
                // </Button>,
                <IconButton
                    key="delete"
                    onClick={() => {
                        handleDeletePourAvailability(params.row);
                        // Handle delete action
                    }}
                >
                    <DeleteForever />
                </IconButton>,
            ],
        },
    ]);

    return (
        <>
            <DataGridCard
                title="Pour Formats List"
                HeaderComponent={() => (
                    <Button variant="contained" onClick={dialogActions.on}>
                        Add Business
                    </Button>
                )}
            >
                <DataGrid
                    columns={columns}
                    disableColumnMenu
                    disableRowSelectionOnClick
                    paginationMode="client"
                    onRowClick={handleRowClick}
                    rows={businessPourAvailabilities}
                />
            </DataGridCard>
            <Dialog
                open={open}
                onClose={dialogActions.off}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth
            >
                <DialogTitle id="alert-dialog-title">
                    Create Pour Format
                </DialogTitle>
                <FormContainer onSuccess={onSubmit} formContext={formContext}>
                    <DialogContent
                        sx={{
                            gap: 2,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <BusinessInput />
                        <TextFieldElement
                            label="Base Price"
                            name="basePrice"
                            fullWidth
                        />
                        <TextFieldElement
                            label="Up Charge"
                            name="upCharge"
                            fullWidth
                        />
                        <TextFieldElement
                            label="Display Order"
                            name="displayOrder"
                            fullWidth
                        />
                        <CheckboxElement label="Is Default" name="isDefault" />
                        <CheckboxElement
                            label="Not Allowed"
                            name="notAllowed"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelCreate} color="inherit">
                            Cancel
                        </Button>

                        <Button
                            color="primary"
                            variant="contained"
                            loading={formContext.formState.isSubmitting}
                            type="submit"
                            disabled={formContext.formState.isSubmitting}
                        >
                            Create
                        </Button>
                    </DialogActions>
                </FormContainer>
            </Dialog>
        </>
    );
};
